import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Plural } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import Popover from '../../ui/Popover';
import BookmarkNotesList from './BookmarkNotesList';
import { Note, Tag } from '../../../types/api';
import { useOptionalBookmarkSelection } from '../../../contexts/BookmarkSelectionContext';

interface BookmarkNotesTagsProps {
  notes?: Note[];
  tags?: Tag[];
  bookmarkId: string;
}

function BookmarkNotesTags({ notes, tags, bookmarkId }: BookmarkNotesTagsProps) {
  const highlightCount = notes?.length || 0;
  const hasNotes = highlightCount > 0;
  const hasTags = tags && tags.length > 0;
  const [isNotesPopoverOpen, setIsNotesPopoverOpen] = useState(false);
  const isBatchMode = useOptionalBookmarkSelection()?.isBatchMode ?? false;

  // Don't render anything if there are no notes or tags
  if (!hasNotes && !hasTags) {
    return null;
  }

  return (
    <div className="bookmark-notes-tags">
      {/* Notes/Highlights */}
      {hasNotes && (
        <span
          className="inline-flex items-center"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <Popover
            placement="bottom-start"
            open={isNotesPopoverOpen}
            onOpenChange={setIsNotesPopoverOpen}
            preventCloseOnContentClick
            className="max-w-[400px]"
            content={
              <div
                data-testid="bookmark-notes-popover"
                className="max-h-80 max-w-[400px] overflow-y-auto"
                onClick={(event) => event.stopPropagation()}
              >
                <BookmarkNotesList notes={notes ?? []} bookmarkId={bookmarkId} />
              </div>
            }
          >
            <button
              type="button"
              className="badge badge-highlight text-bookmark-item-badge"
              title={t`Show notes`}
            >
              <Plural value={highlightCount} one="# note" other="# notes" />
            </button>
          </Popover>
        </span>
      )}

      {/* Tags */}
      {hasTags &&
        tags.map((tag) =>
          isBatchMode ? (
            <span key={tag.id} className="badge badge-tag text-bookmark-item-tag">
              {tag.name}
            </span>
          ) : (
            <Link
              key={tag.id}
              to="/tags/$slug"
              params={{ slug: tag.slug }}
              className="badge badge-tag text-bookmark-item-tag"
            >
              {tag.name}
            </Link>
          ),
        )}
    </div>
  );
}

export default BookmarkNotesTags;
